import React, { useState, useMemo, useCallback } from "react";
import { useActiveLeague } from "../../hooks/useActiveLeague";
import { useCurrentTeamId } from "../../hooks/useCurrentTeamId";
import Flag from "../common/Flag";
import { Link } from "react-router-dom";

import { 
  ATTRIBUTE_MAPPING, 
  OUTFIELD_STATS_FIELDS, 
  GOALKEEPER_STATS_FIELDS,
  getAttributeValueClass,
  numToPrice,
  sortArrayByKey,
  getColumnClass
} from "../utils";

function StatsTable({ 
  players, 
  handlePopup, 
  setSelectedPlayer,
  onBidPlayer,
  unsignable = false,
  // NEW: Backend integration props
  onSort,                    // Callback to trigger backend sort
  currentSortBy,             // Current sort field from parent
  currentSortType,           // Current sort direction from parent
  showTooltips = true        // Enable/disable tooltips
}) {
  const { activeLeague } = useActiveLeague();
  const currentTeamId = useCurrentTeamId();
  // Use players directly - no local sorting
  const displayPlayers = useMemo(
    () => players.filter((item) => item.currentTeam?.id !== currentTeamId),
    [players, currentTeamId]
  );

  // Backend sort handler
  const handleSort = useCallback((key) => {
    if (onSort) {
      // Convert key to attribute name if needed
      const fullAttrName = ATTRIBUTE_MAPPING[key] || key;
      const newOrder = currentSortBy === fullAttrName 
        ? (currentSortType === "asc" ? "desc" : "asc")
        : "desc";
      onSort(`${fullAttrName}-${newOrder}`);
    }
  }, [onSort, currentSortBy, currentSortType]);

  // Enhanced additionalClass to include attr-col for attributes
  const additionalClass = (key) => {
    const baseClass = getColumnClass(key);
    if (ATTRIBUTE_MAPPING[key]) return baseClass + " attr-col";
    return baseClass;
  };

  const handlePlayerSelect = useCallback(async (player, event) => {
    console.log('=== STATS TABLE BID BUTTON CLICKED ===');
    console.log('Player:', player.name);
    console.log('Event:', event);
    console.log('Event target:', event.target);
    
    // Prevent event from bubbling up to the row click handler
    event.preventDefault();
    event.stopPropagation();
    
    if (onBidPlayer) {
      console.log('Opening bid popup via parent handler...');
      await onBidPlayer(player);
    } else {
      if (setSelectedPlayer) {
        console.log('Setting selected player...');
        setSelectedPlayer(player);
      }
      if (handlePopup) {
        console.log('Calling handlePopup with bid...');
        handlePopup('bid');
      }
    }
    
    console.log('=== STATS TABLE BID BUTTON CLICK COMPLETE ===');
  }, [onBidPlayer, setSelectedPlayer, handlePopup]);

  // Add this handler before the offerItems mapping (around Line 55)
  const handleRowClick = useCallback((wyId, event) => {
    console.log('=== ROW CLICK DETECTED ===');
    console.log('Event target:', event.target);
    console.log('Event current target:', event.currentTarget);
    
    // Prevent navigation if clicking on interactive elements
    const clickedElement = event.target.closest('.bid-btn, .unsign-btn, .ico, button, a');
    console.log('Clicked element found:', clickedElement);
    
    if (clickedElement) {
      console.log('Row click prevented - interactive element clicked');
      return; // Let the original handler deal with it
    }
    
    console.log('Opening player profile for wyId:', wyId);
    // Open player profile in new tab
    const urlBase = activeLeague?.slug ? `/${activeLeague.slug}` : '';
    const leagueQuery = activeLeague?._id ? `?leagueId=${activeLeague._id}` : '';
    window.open(`${urlBase}/player/${wyId}${leagueQuery}`, '_blank');
  }, []);

  // Determine which fields to show based on player mix
  const hasGoalkeepers = useMemo(() => 
    players.some(p => p.position === 'GKP'), [players]
  );
  const hasOutfield = useMemo(() => 
    players.some(p => p.position !== 'GKP'), [players]
  );
  
  // Use appropriate fields based on player composition
  const statsFields = hasGoalkeepers && !hasOutfield 
    ? GOALKEEPER_STATS_FIELDS 
    : OUTFIELD_STATS_FIELDS;

  const offerItems = displayPlayers.map(function (item) {
    const unsignButton = () => {
      if (unsignable) {
        return <button className="unsign-btn ico icon-r-b-cross" />;
      }
      return "";
    };

    // Determine player-specific fields to use - Updated to check for GKP
    const playerIsGoalkeeper = item.position === 'GKP';
    const playerFields = playerIsGoalkeeper ? GOALKEEPER_STATS_FIELDS : OUTFIELD_STATS_FIELDS;
    
    return (
      <div 
        key={item.wyId} 
        className="row clickable-row"
        onClick={(e) => handleRowClick(item.wyId, e)}
      >
        {/* Use player-specific fields instead of shared statsFields */}
        {Object.entries(playerFields).map(([key, value]) => {
          const itemVal = () => {
            if (key === "value") {
              return numToPrice(item[key], "€");
            }
            if (key === "nationality") {
              return (
                <div className="image">
                  <Flag code={item.nationalityCode || item[key]} fallback={item[key]} />
                </div>
              );
            }
            if (ATTRIBUTE_MAPPING[key]) {
              // Handle attribute values
              const attrValue = item.attributes?.[ATTRIBUTE_MAPPING[key]] || 0;
              return (
                <span className={`stats-attr-value ${getAttributeValueClass(attrValue)}`}>
                  {attrValue}
                </span>
              );
            }
            return item[key];
          };

          return (
            <div key={key} className={`col ${additionalClass(key)}`}>
              {itemVal()}
            </div>
          );
        })}

        <div key={"ctrls-column"} className="col ctrls-col">
          <Link to={`${activeLeague?.slug ? `/${activeLeague.slug}` : ''}/player/${item.wyId}${activeLeague?._id ? `?leagueId=${activeLeague._id}` : ''}`} className="ico icon-tip" />
          {unsignButton()}
          {!unsignable && (
            <button
              className="bid-btn"
              style={{ pointerEvents: 'auto', zIndex: 100 }}
              type="button"
              onClick={(event) => {
                console.log('Stats table button raw click detected');
                handlePlayerSelect(item, event);
              }}
              onMouseDown={(e) => console.log('Stats table button mouse down')}
              onMouseUp={(e) => console.log('Stats table button mouse up')}
            >
              BID
            </button>
          )}
        </div>
      </div>
    );
  });

  return (
    <div className="stats-table">
      <div className="row head-row">
        {Object.entries(statsFields).map(([key, value], index) => {
          const isCurrentSort = (ATTRIBUTE_MAPPING[key] || key) === currentSortBy;
          return (
            <div key={index} className={`col ${additionalClass(key)}`}>
              <button
                type="button"
                className={`sort-btn ${isCurrentSort ? currentSortType : ""}`}
                title={showTooltips ? (ATTRIBUTE_MAPPING[key] || value) : undefined}
                onClick={() => handleSort(key)}
              >
                {value}
              </button>
            </div>
          );
        })}
        <div key={"ctrls-column"} className="col ctrls-col" />
      </div>
      {offerItems}
    </div>
  );
}

export { StatsTable };
